import { z } from "zod";

export type AuditUrlResult =
  | { ok: true; value: string }
  | { ok: false; message: string };

const URL_ERROR = "Enter a valid public website URL - for example, https://yourcompany.com";

function isPrivateOrLocalHost(hostname: string): boolean {
  const host = hostname.toLowerCase().replace(/\.$/, "");

  if (host === "localhost" || host.endsWith(".localhost") || host.endsWith(".local")) return true;
  if (host === "::1" || host === "[::1]") return true;

  const ipv4 = host.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (!ipv4) return false;

  const octets = ipv4.slice(1).map(Number);
  if (octets.some((octet) => octet > 255)) return true;
  const [first, second] = octets;
  return first === 0 || first === 10 || first === 127 ||
    (first === 169 && second === 254) ||
    (first === 172 && second >= 16 && second <= 31) ||
    (first === 192 && second === 168);
}

/**
 * Converts common public website input into one canonical HTTPS URL before an
 * audit starts. Deeper DNS and SSRF protection remains enforced server-side.
 */
export function normalizeAuditWebsiteUrl(rawValue: string): AuditUrlResult {
  const raw = rawValue.trim();
  if (!raw) return { ok: false, message: URL_ERROR };

  const candidate = /^[a-z][a-z\d+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`;

  try {
    const parsed = new URL(candidate);
    if (!/^https?:$/.test(parsed.protocol) || parsed.username || parsed.password || isPrivateOrLocalHost(parsed.hostname)) {
      return { ok: false, message: URL_ERROR };
    }

    // Audit fetches use HTTPS; normalize harmless http input before the OTP flow.
    if (parsed.protocol === "http:") parsed.protocol = "https:";
    return { ok: true, value: parsed.toString() };
  } catch {
    return { ok: false, message: URL_ERROR };
  }
}

export const auditWebsiteUrlSchema = z.string().transform((value, context) => {
  const normalized = normalizeAuditWebsiteUrl(value);
  if (!normalized.ok) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: normalized.message });
    return z.NEVER;
  }
  return normalized.value;
});
